import { Field, Form } from 'formik';
import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { getProject } from 'features/project/projectSlice';
import {
  resetHypothesis,
  updateSelectionVersion,
} from 'features/hypotheses/hypothesisSlice';
import AutoSave from 'common/components/AutoSave.tsx';
import FormActions from 'common/components/FormActions/index.tsx';
import { Icon, PageNotViewable, Button } from '@pwc/pwc-deals-ui';
import { useEventSubscription } from '@pwc/event-hub-ui';
import { useDirtyCheck } from '@pwc/auto-save-ui';
import { CommentTarget } from '@pwc/pwc-deals-web-common';
import { Link, useHistory } from 'react-router-dom';
import featureFlags from 'common/helpers/featureFlags';

import { getFakeClientUser } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import ActiveContentHeader from 'common/components/ContentHeaders/ActiveContentHeader';
import { FormLayout } from 'common/components/layouts';
import { LinkedContentNavigation } from 'common/components/LinkedContentNavigation/LinkedContentNavigation';
import RichTextBalloonEditorFormikField from 'common/components/RichTextBalloonEditorFormikField/RichTextBalloonEditorFormikField';
import { balloonEditorConfig } from 'common/components/RichTextEditor/CKEditorConfig';
import { EmptyClipboardModal } from 'common/components/EmptyClipboardModal';
import SectionLayoutMain from 'common/components/SectionLayout/SectionLayoutMain';
import Separator from 'common/components/Separator';
import TitleFieldsetWithWordCount from 'common/components/TitleFieldWithWordCount/TitleFieldWithWordCount';
import VersionToggleContainer from 'common/components/VersionToggle/VersionToggleContainer';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import useMobile from 'common/hooks/useMobile';
import { useSections } from 'common/components/SectionLayout/SectionContext';
import { ConnectedContentContextProvider } from 'features/narratives/Context/ConnectedContentContext';
import EditConnectedContentNew from 'features/narratives/EditConnectedContentNew/EditConnectedContentNew';
import useAllConnections from 'features/narratives/hooks/useAllConnections';
import { contentIsLinked } from 'features/narratives/narrativesUtilities';
import TagEditor from 'features/tagging/common/TagEditor/TagEditor';
import {
  AUTO_SAVE_IDLE_ERROR,
  AUTO_SAVE_LOCK_ERROR,
} from 'common/components/GlobalError/constants';
import { CKEditorContext } from 'common/contexts/CKEditorContext.tsx';
import {
  hypothesesListPath,
  viewHypothesisPath,
} from '../../../common/services/urlService';
import useSearchTitleDuplicity from './useSearchTitleDuplicity.ts';
import Thumbnail from '../Thumbnail/Thumbnail';

import './HypothesisForm.scss';

const HypothesisForm = (props) => {
  const isMobile = useMobile();
  const history = useHistory();
  const project = useSelector(getProject);
  const { t } = useTranslation();
  const isFakeClient = useSelector(getFakeClientUser);

  const {
    onDelete,
    onCancel,
    isSaving,
    isValid,
    children,
    projectId,
    parentContentId,
    currentlyVisibleConnections,
    setCurrentlyVisibleConnections,
    dirty,
    updateThumbnail,
    setConnectedContentChanged,
    thumbnail,
    loadingThumbnail,
    loadingItems = false,
    draftVersion,
    clientVersion,
    commentCounts,
    previewAction,
    submitForm,
    values,
    isAutoSaveCalled = false,
    isCreateMode,
    setAutoSaveCalled = () => {},
    handleRedirectToView,
    isSavingFulfilled = false,
    isSavingRejected = false,
    contentThreadId,
    projectName,
    ...rest
  } = props;
  const isMountedRef = useIsMountedRef();
  const isPublishable = !isSaving && isValid && !!dirty;
  const dispatch = useDispatch();

  useEffect(() => {
    if (isFakeClient) {
      if (!clientVersion && draftVersion) {
        history.push(hypothesesListPath(projectId));
      } else if (clientVersion && draftVersion) {
        dispatch(resetHypothesis());
        const path = viewHypothesisPath(projectId, contentThreadId);

        history.push(path);
      }
    }
  }, [
    draftVersion,
    clientVersion,
    projectId,
    history,
    isFakeClient,
    contentThreadId,
    dispatch,
  ]);

  const [noDataOnClipboard, setNoDataOnClipboard] = useState(false);
  const [connectedAnalyses, setConnectedAnalyses] = useState([]);

  const { allConnections: allRetrievedConnections, error } = useAllConnections(
    isMountedRef.current
  );

  const [{ currentSections }] = useSections();

  const { title } = values;
  const draftContentId = draftVersion?.contentId || null;
  if (draftVersion) {
    dispatch(updateSelectionVersion(draftVersion));
  }
  const clientContentId = clientVersion?.contentId || null;
  const { titleError } = useSearchTitleDuplicity({
    title,
    projectId,
    draftContentId,
    clientContentId,
  });

  const currentForm = {
    title: values.title,
    body: values.body,
    insight: values.insight,
    tags: values.tags,
    connectedAnalyses,
    initialConnectedAnalyses: connectedAnalyses,
    thumbnailId: (thumbnail && thumbnail.id) || null,
    section: currentSections.map((section) => section).flatMap((s) => s.items),
    layoutType: currentSections.map((section) => section.layoutType),
  };

  const [initialForm, setInitialForm] = useState(null);
  const dirtyCheck = useDirtyCheck(initialForm);

  useEventSubscription('autosave-save', (_event) => {
    if (titleError) {
      return;
    }
    submitForm();
    dirtyCheck.setLastData(currentForm);
  });

  useEventSubscription('autosave-idle', (_event) => {
    handleRedirectToView(AUTO_SAVE_IDLE_ERROR);
  });

  useEffect(() => {
    if (
      (isAutoSaveCalled || !currentlyVisibleConnections) &&
      allRetrievedConnections
    ) {
      const findThisAnalysis = Object.keys(allRetrievedConnections).map(
        (key) => {
          const analysesWithoutParent = allRetrievedConnections[key].filter(
            (obj) => {
              if (obj.contentId !== parentContentId) {
                return obj;
              }

              return analysesWithoutParent;
            }
          );

          return { [key]: analysesWithoutParent };
        }
      );

      const objectConnections = Object.assign({}, ...findThisAnalysis);

      setCurrentlyVisibleConnections(objectConnections);

      if (isAutoSaveCalled) {
        setAutoSaveCalled(false);
      }
    }
  }, [
    allRetrievedConnections,
    currentlyVisibleConnections,
    setCurrentlyVisibleConnections,
    parentContentId,
    isAutoSaveCalled,
    setAutoSaveCalled,
  ]);

  useEffect(
    () => {
      const filtered = [];
      if (currentlyVisibleConnections && currentlyVisibleConnections.analyses) {
        currentlyVisibleConnections.analyses.filter((content) => {
          if (contentIsLinked(content.links, parentContentId)) {
            filtered.push(content);
          }
          return [];
        });
      }
      if (!isEqual(filtered, connectedAnalyses)) {
        setConnectedAnalyses(filtered);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentlyVisibleConnections,
      parentContentId,
      allRetrievedConnections,
      setConnectedContentChanged,
      setCurrentlyVisibleConnections,
    ]
  );

  const handleDone = async () => {
    if (
      dirtyCheck.dataIsDirty({
        ...currentForm,
        initialConnectedAnalyses: connectedAnalyses,
      })
    ) {
      await submitForm();
    }

    handleRedirectToView();
  };

  const isAutoSaveReady =
    !titleError && values && !loadingThumbnail && !loadingItems;

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isAutoSaveReady && !initialForm) {
      setInitialForm({
        title: values.title,
        body: values.body,
        insight: values.insight,
        tags: values.tags,
        connectedAnalyses,
        initialConnectedAnalyses: values.connectedAnalyses,
        thumbnailId: (thumbnail && thumbnail.id) || null,
        section: currentSections
          .map((section) => section)
          .flatMap((s) => s.items),
        layoutType: currentSections.map((section) => section.layoutType),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoSaveReady, values, connectedAnalyses, initialForm, thumbnail]);

  if (isMobile) {
    return (
      <PageNotViewable>
        <Link to={hypothesesListPath(projectId)}>
          <Button className="full-width-mobile">{t('backToKFA')}</Button>
        </Link>
      </PageNotViewable>
    );
  }

  // prevent react invalid prop on element
  // eslint-disable-next-line no-unused-vars
  const { errorType, ...passedToForm } = rest;

  return (
    <AutoSave
      diableAutoSaveOverride={isCreateMode}
      projectId={projectId}
      contentThreadId={contentThreadId}
      isReady={isAutoSaveReady}
      isValid={!titleError}
      initialData={initialForm}
      getData={() => currentForm}
    >
      {({ acquireLockState: { isError } }) => {
        if (isError) {
          handleRedirectToView(AUTO_SAVE_LOCK_ERROR);
          return null;
        }
        return (
          <div className="kfa-form-wrap">
            {/* <CommentingEventListener contentId={draftContentId} /> */}
            <FormLayout
              headerComponent={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <div className="toggle-version-container-edit-mode">
                  <VersionToggleContainer
                    config={{
                      commentCounts,
                      draftVersion,
                      sharedVersion: clientVersion,
                    }}
                    hideTextVersion={featureFlags.isVersioningPhase1Enabled}
                    isEntitySharedWithClient={false}
                    mode="edit"
                    projectId={projectId}
                  />
                  <ActiveContentHeader>
                    <FormActions
                      isPreviewHidden
                      cancelAction={onCancel}
                      reportAction={previewAction}
                      doneAction={handleDone}
                      deleteAction={onDelete}
                      isReportDisabled={!isPublishable || titleError}
                      isDoneDisabled={!isPublishable || titleError}
                      isDeleteDisabled={!isPublishable || titleError}
                      position="top"
                      isCreateMode={isCreateMode}
                      isSaving={isSaving}
                      isSavingFulfilled={isSavingFulfilled}
                      isSavingRejected={isSavingRejected}
                      projectName={projectName}
                      itemTitle={title}
                    />
                  </ActiveContentHeader>
                </div>
              }
            >
              {children}

              <Form
                data-testid="hypothesis-form"
                className="mt-4 kfa-form-wrap"
                {...passedToForm}
              >
                <div className="form-fields">
                  <div className="input-container-st">
                    <div
                      className="kfa__contents__details"
                      data-testid="kfa-card-details"
                    >
                      <div
                        className="kfa__contents__details__title"
                        data-testid="kfa-card-title"
                      >
                        <Field
                          component={TitleFieldsetWithWordCount}
                          name="title"
                          placeholder={t('titleRequired')}
                          suggestedLength={100}
                          dataTestId="text-area-title"
                          errorMessage={titleError}
                        />

                        <Field
                          config={balloonEditorConfig}
                          component={RichTextBalloonEditorFormikField}
                          suggestedLength={500}
                          dataTestId="text-area-conclusion"
                          label={t('insightOptional')}
                          name="insight"
                          withContainer
                          containerLabel={t('insight')}
                          containerIcon="light-bulb"
                          commentTarget={CommentTarget.Insight}
                          showCommentIcon
                        />
                      </div>
                    </div>
                    <div className="kfa__contents__analyses-list">
                      <LinkedContentNavigation
                        projectId={projectId}
                        connectedAnalyses={connectedAnalyses}
                        hypothesisIndex={1}
                        isKfa
                        parentContentId={parentContentId}
                        useParentConnections
                        parentAllRetrievedConnections={allRetrievedConnections}
                        parentError={error}
                        isCommentingAllowedOnThisItem
                      />
                    </div>
                  </div>

                  <div className="thumbnail-form-wrap">
                    <Thumbnail
                      projectId={projectId}
                      setNoDataOnClipboard={setNoDataOnClipboard}
                      updateThumbnail={updateThumbnail}
                      thumbnail={thumbnail}
                      loading={loadingThumbnail}
                    />
                  </div>

                  <div className="kfa-description-wrapper">
                    <Field
                      config={balloonEditorConfig}
                      component={RichTextBalloonEditorFormikField}
                      suggestedLength={320}
                      dataTestId="text-area-body"
                      label={t('descriptionOptional')}
                      name="body"
                      commentTarget={CommentTarget.Description}
                      showCommentIcon
                    />
                  </div>

                  <div className="kfa-detail-container">
                    <div className="kfa-detail-title">
                      <div className="d-flex mt-5 justify-content-between">
                        <div className="d-flex mb-1">
                          <div className="mr-3">
                            <Icon name="text" width="16" />
                          </div>
                          <div className="block-title">{t('detail')}</div>
                        </div>
                      </div>
                      <p className="description">{t('detailExplanation')}</p>
                    </div>
                    <div>
                      <CKEditorContext.Provider
                        value={{
                          enableImageUpload:
                            featureFlags.isImageUploadEnabled &&
                            project.ImageIngestionEnabled === 'true',
                        }}
                      >
                        <SectionLayoutMain loading={loadingItems} />
                      </CKEditorContext.Provider>
                    </div>
                  </div>
                  <Separator />
                </div>

                <div className="hypothesis-form-extras">
                  <TagEditor projectId={projectId} isCommentingAllowed />
                  <ConnectedContentContextProvider>
                    <EditConnectedContentNew
                      lastItem
                      projectId={projectId}
                      parentContentId={parentContentId}
                      setCurrentlyVisibleConnections={
                        setCurrentlyVisibleConnections
                      }
                      allConnections={currentlyVisibleConnections}
                      connectedContentType="analyses"
                      setConnectedContentChanged={setConnectedContentChanged}
                      currentAnalysis={{}}
                      error={error}
                      isCommentingAllowedOnThisItem
                      pageContainerName="kfa-form-wrap"
                      setConnectedAnalyses={setConnectedAnalyses}
                    />
                  </ConnectedContentContextProvider>
                  <FormActions
                    isPreviewHidden
                    cancelAction={onCancel}
                    reportAction={previewAction}
                    doneAction={handleDone}
                    isReportDisabled={!isPublishable || titleError}
                    isDoneDisabled={!isPublishable || titleError}
                    position="bottom"
                    isCreateMode={isCreateMode}
                    contentType="kfa"
                    isSaving={isSaving}
                    isSavingFulfilled={isSavingFulfilled}
                  />
                </div>
              </Form>
              {noDataOnClipboard && (
                <>
                  <EmptyClipboardModal
                    show={noDataOnClipboard}
                    onHide={() => setNoDataOnClipboard(false)}
                  />
                </>
              )}
            </FormLayout>
          </div>
        );
      }}
    </AutoSave>
  );
};

export default HypothesisForm;
